import { useEffect, useState } from 'react';
import { 
  Grid, 
  Divider, 
  Alert,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled
} from '@mui/material';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import { Check } from '@mui/icons-material';
import { toast } from 'react-toastify';

import api from 'src/Api';

import Step1 from './client_steps/Step1';
import Step2 from './client_steps/Step2';
import Step6 from './client_steps/Step6';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 22,
      left: 'calc(-50% + 20px)',
      right: 'calc(50% + 20px)',
      padding: '0px 20px',
    },

    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette.primary.main,
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 8, // Épaisseur ici
      border: 0,
      borderRadius: 4,
      backgroundColor: '#eaeaf0', // couleur par défaut (non actif)
      transition: 'background-color 0.3s ease',
      ...theme.applyStyles?.('dark', {
        backgroundColor: theme.palette.grey[800],
      }),
    },
  }));

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.grey[300],
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main, // Amber/gold color for active step
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.1)',
  }),
  ...(ownerState.completed && {
    backgroundColor: theme.palette.primary.main,
    color:'#fff',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.1)',
  }),
}));

// Custom step icon component
function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      <Box sx={{ 
        fontSize: 24, 
        fontWeight: 'bold',
        color: active ? 'white' : theme => theme.palette.text.secondary
      }}>
        {icon}
      </Box>
    </ColorlibStepIconRoot>
  );
}

export default function ClientStepper({ user, setUser }) {
  const [activeStep, setActiveStep] = useState(0); // Material UI Stepper uses 0-based index
  const [unVerifiedSteps, setUnVerifiedSteps] = useState([...user?.unverified_steps]);
  
  // Map the 1-based steps from original code to 0-based steps for Material UI
  const getComponentStep = () => {
    switch(activeStep) {
      case 0: return 1; // UI Step 1 maps to component Step 1
      case 1: return 2; // UI Step 2 maps to component Step 2
      case 2: return 6; // UI Step 3 maps to component Step 6
      default: return 1;
    }
  };

  // Handle step click - only allow if not disabled
  const handleStepClick = (step) => {
    // Convert UI step (0-based) to component step (1-based for steps 1 and 2, 6 for step 3)
    const componentStep = step === 0 ? 1 : step === 1 ? 2 : 6;
    
    // Check if step should be disabled
    if (!(unVerifiedSteps.includes(1) && componentStep > 1) && 
        !(unVerifiedSteps.includes(2) && componentStep > 2)) {
      setActiveStep(step);
    }
  };

  const showError = (msg) => {
    toast.error(msg, {
      position: 'top-center',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const showSuccess = (msg) => {
    toast.success(msg, {
      position: 'top-center',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const submit = () => {
    if (unVerifiedSteps.length === 1 && unVerifiedSteps.includes(-1)) {
      api.post('/users/submit_step', { step: -1 })
        .then((res) => {
          if (res.data.error === 'wait') {
            //showSuccess(
            //    "Vos documents seront vérifiés par l'administration dans un délai de 2 jours ouvrés"
            //);
          } else {
            setUser(res.data);
          }
        })
        .catch(() => {
          showError("Une erreur s\'est produite.");
        });
    }
  };

  useEffect(() => {
    if (unVerifiedSteps.length === 1 && unVerifiedSteps.includes(-1)) {
      submit();
    }
  }, [unVerifiedSteps]);

  // Define steps
  const steps = [
    "Logo de l'entreprise",
    "Informations générales",
    "Adresse"
  ];

  // Handle next step logic
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  return (
    <Grid container spacing={2} direction="column">
      {/* Alert Section */}
      {unVerifiedSteps.length === 1 && unVerifiedSteps.includes(-1) && (
        <Grid item xs={12}>
          <Alert icon={<Check fontSize="inherit" />} severity="success">
            Vous venez de finaliser votre inscription 🥳 Une dernière étape : votre demande sera <br />
            étudiée par notre équipe dans les plus brefs délais. Merci de votre patience.
          </Alert>
        </Grid>
      )}

      {/* Stepper */}
      <Grid item xs={12}>
      <Box sx={{ width: '60%', mt: 2, mb: 4, mx: 'auto' }}>
          <Stepper 
            alternativeLabel 
            activeStep={activeStep}
            connector={<ColorlibConnector />}
          >
            {steps.map((label, index) => {
              // Determine if the step should be disabled
              const stepDisabled = 
                (index === 1 && unVerifiedSteps.includes(1)) || 
                (index === 2 && (unVerifiedSteps.includes(1) || unVerifiedSteps.includes(2)));
              
              return (
                <Step 
                  key={label}
                  completed={
                    index === 0 ? !unVerifiedSteps.includes(1) : 
                    index === 1 ? !unVerifiedSteps.includes(2) : 
                    !unVerifiedSteps.includes(6)
                  }
                  onClick={() => !stepDisabled && handleStepClick(index)}
                  sx={{ 
                    cursor: stepDisabled ? 'not-allowed' : 'pointer',
                    opacity: stepDisabled ? 0.7 : 1
                  }}
                >
<StepLabel 
  StepIconComponent={ColorlibStepIcon}
  sx={{ 
    '.MuiStepLabel-label': { 
      mt: 1,
      color: stepDisabled ? 'text.disabled' : '#fff'
    },
    '.MuiStepLabel-label.Mui-completed': {
      color: '#fff', // texte en blanc pour les étapes complétées
    },
  }}
>
  {/* {label} */}
</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      </Grid>



      {/* Step Content */}
      <Grid item xs={12}>
        {activeStep === 0 && (
          <Step1
            setActiveStep={() => handleNext()}
            user={user}
            setUser={setUser}
            setUnVerifiedSteps={setUnVerifiedSteps}
          />
        )}
        {activeStep === 1 && (
          <Step2
            setActiveStep={() => handleNext()}
            user={user}
            setUnVerifiedSteps={setUnVerifiedSteps}
          />
        )}
        {activeStep === 2 && (
          <Step6
            setActiveStep={() => handleNext()}
            user={user}
            setUser={setUser}
            setUnVerifiedSteps={setUnVerifiedSteps}
          />
        )}
      </Grid>
    </Grid>
  );
}